import type { TitleFilter } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { MaybeRefOrGetter } from '@vueuse/core';

import type {
	GetSingleQtvSimilarTitlesQuery,
	GetSingleQtvSimilarTitlesQueryVariables,
} from '@/features/buybox/graphql/queries/GetSingleQTV.query';

import { GetSingleQtvSimilarTitlesDocument } from '@/features/buybox/graphql/queries/GetSingleQTV.query';

import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { SimilarTitlesSorting } from '@/@types/graphql-types';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';

import { toValue } from '@vueuse/core';
import { computed } from 'vue';

const singleQTVBannerAndDisclaimer: Record<string, Record<string, { bannerText: string; disclaimerText: string }>> = {};

export function useSingleQTV(
	title: MaybeRefOrGetter<TitleDetail>,
	providerShortName: MaybeRefOrGetter<string | null>,
	enabled: MaybeRefOrGetter<boolean>,
	experimentFilters?: MaybeRefOrGetter<TitleFilter | null>
) {
	const { country, language } = useLanguageStore();
	const _title = toValue(title);

	const shortName = computed(() => toValue(providerShortName) || '');
	const variables = computed<GetSingleQtvSimilarTitlesQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		first: 1,
		titleId: _title.__typename === 'Season' ? _title.show.id : _title.id,
		filters: {
			packages: [shortName.value],
			...(toValue(experimentFilters) ?? {}),
		},
		offerFilters: {
			packages: [shortName.value],
			bestOnly: true,
		},
		sorting: SimilarTitlesSorting['ImdbScore'],
	}));

	const enableQuery = computed(() => toValue(enabled) && toValue(providerShortName) !== null);

	const { result, loading, onError } = useQuery<
		GetSingleQtvSimilarTitlesQuery,
		GetSingleQtvSimilarTitlesQueryVariables
	>(GetSingleQtvSimilarTitlesDocument, variables, { enabled: enableQuery });

	onError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvSimilarTitlesQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const sqtvBannerAndDisclaimer = computed(
		() => singleQTVBannerAndDisclaimer[country.value]?.[shortName.value] ?? null
	);

	const similarTitle = computed(() => {
		return result.value?.node?.__typename === 'Movie' || result.value?.node?.__typename === 'Show'
			? result?.value?.node?.similarTitlesV2.edges?.[0]
			: null;
	});

	const isSQTVEmpty = computed(() => {
		if (loading.value) return false;

		return similarTitle.value == null;
	});

	return {
		similarTitle,
		loading,
		isSQTVEmpty,
		sqtvBannerAndDisclaimer,
	};
}
